import type { LaneInterval, LaneRowData, LocationSample } from '../historyGraph/types'

/** Single blue color used for all geofence rows */
const GEOFENCE_BLUE = '#3b82f6'

/**
 * Build geofence rows for the timeline:
 * - An aggregate "Geofence In/Out" row: gray when inside ANY geofence, blank when outside
 * - Per-geofence rows: blue when inside, blank when outside
 */
export function buildGeofenceRows(locations: LocationSample[]): {
  aggregateRow: LaneRowData | null
  perGeofenceRows: LaneRowData[]
} {
  if (!locations?.length) return { aggregateRow: null, perGeofenceRows: [] }

  const aggregateIntervals: LaneInterval[] = []
  const byName = new Map<string, LaneInterval[]>()

  let insideStart: number | null = null
  let currentGeofence: string | null = null
  let gfStart: number | null = null

  for (let i = 0; i < locations.length; i++) {
    const time = new Date(locations[i].time).getTime()
    const gfName = locations[i].geofence?.name ?? null

    // Aggregate: inside any geofence
    if (gfName && insideStart === null) {
      insideStart = time
    } else if (!gfName && insideStart !== null) {
      aggregateIntervals.push({ start: insideStart, end: time, value: 'in' })
      insideStart = null
    }

    // Per-geofence tracking
    if (gfName !== currentGeofence) {
      // Close previous geofence interval
      if (currentGeofence && gfStart !== null) {
        if (!byName.has(currentGeofence)) byName.set(currentGeofence, [])
        byName.get(currentGeofence)!.push({ start: gfStart, end: time, value: 'in' })
      }
      // Open new geofence interval
      if (gfName) {
        gfStart = time
      } else {
        gfStart = null
      }
      currentGeofence = gfName
    }
  }

  // Close any open intervals at end
  const lastTime = new Date(locations[locations.length - 1].time).getTime()
  if (insideStart !== null) {
    aggregateIntervals.push({ start: insideStart, end: lastTime, value: 'in' })
  }
  if (currentGeofence && gfStart !== null) {
    if (!byName.has(currentGeofence)) byName.set(currentGeofence, [])
    byName.get(currentGeofence)!.push({ start: gfStart, end: lastTime, value: 'in' })
  }

  const aggregateRow: LaneRowData | null = aggregateIntervals.length
    ? {
        key: 'geofence_aggregate',
        label: 'Geofence In/Out',
        colorMapKey: 'geofenceColor',
        intervals: aggregateIntervals.map((iv) => ({ ...iv, value: GEOFENCE_BLUE })),
      }
    : null

  const perGeofenceRows: LaneRowData[] = []
  for (const [name, intervals] of byName) {
    perGeofenceRows.push({
      key: `geofence_${name}`,
      label: `In ${name}`,
      colorMapKey: 'geofenceColor',
      intervals: intervals.map((iv) => ({ ...iv, value: GEOFENCE_BLUE })),
    })
  }

  return { aggregateRow, perGeofenceRows }
}
